import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import createSafeContext from '@paradigm/utils/createSafeContext';

export type TerminalTab = 'chat' | 'examples' | 'cron';

/**
 * LaunchDarkly flag that gates the entire Block Terminal feature — both
 * the bottom-panel tab and the right-rail side-panel mount.
 */
export const FEATURE_BLOCK_TERMINAL = 'feature_block_terminal';

/**
 * Where the terminal docks when open.
 *
 *   - `bottom-tab` — embedded in the BottomPanel as one of the tab views
 *     (the historical Paradigm Block default).
 *   - `side-panel` — fixed-position vertical strip on the right edge of the
 *     workspace; doesn't share space with the bottom panel.
 */
export type PinMode = 'bottom-tab' | 'side-panel';

interface Actions {
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
  readonly setPinMode: (mode: PinMode) => void;
  readonly setActiveTab: (tab: TerminalTab) => void;
}

interface State {
  readonly isOpen: boolean;
  readonly pinMode: PinMode;
  readonly activeTab: TerminalTab;
}

const PIN_MODE_KEY = 'paradigm_terminal_pin_mode';
const OPEN_KEY = 'paradigm_terminal_open';
const ACTIVE_TAB_KEY = 'paradigm_terminal_active_tab';

function loadBoolean(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === 'true';
  } catch {
    return fallback;
  }
}

function loadPinMode(): PinMode {
  try {
    const v = localStorage.getItem(PIN_MODE_KEY);
    return v === 'side-panel' ? 'side-panel' : 'bottom-tab';
  } catch {
    return 'bottom-tab';
  }
}

function loadActiveTab(): TerminalTab {
  try {
    const v = localStorage.getItem(ACTIVE_TAB_KEY);
    if (v === 'examples' || v === 'cron') return v;
    return 'chat';
  } catch {
    return 'chat';
  }
}

function loadInitialState(): State {
  return {
    isOpen: loadBoolean(OPEN_KEY, false),
    pinMode: loadPinMode(),
    activeTab: loadActiveTab(),
  };
}

export const [useTerminalDrawerState, StateProvider] = createSafeContext<State>(
  'TerminalDrawerState',
);

export const [useTerminalDrawerActions, ActionsProvider] =
  createSafeContext<Actions>('TerminalDrawerActions');

interface Props {
  readonly children: ReactNode;
}

export function TerminalDrawerProvider({ children }: Props) {
  const [state, setState] = useState(loadInitialState);

  const actions = useMemo(() => createActions(setState), []);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, String(state.isOpen));
    } catch {
      // storage unavailable (private mode) — ignore
    }
  }, [state.isOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(PIN_MODE_KEY, state.pinMode);
    } catch {
      // ignore
    }
  }, [state.pinMode]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, state.activeTab);
    } catch {
      // ignore
    }
  }, [state.activeTab]);

  // Toggle drawer with ` (backtick/tilde). Cmd/Ctrl+` works from anywhere
  // including inputs, so users can close while typing in chat. Plain `
  // skips editable targets to avoid hijacking literal backticks.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== '`' && e.key !== '~') return;
      if (e.altKey) return;
      const withModifier = e.metaKey || e.ctrlKey;
      if (!withModifier) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target?.isContentEditable === true;
        if (isEditable) return;
      }
      e.preventDefault();
      actions.toggle();
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [actions]);

  return (
    <StateProvider value={state}>
      <ActionsProvider value={actions}>{children}</ActionsProvider>
    </StateProvider>
  );
}

function createActions(setState: Dispatch<SetStateAction<State>>): Actions {
  return {
    open() {
      setState((prev) => ({ ...prev, isOpen: true }));
    },
    close() {
      setState((prev) => ({ ...prev, isOpen: false }));
    },
    toggle() {
      setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
    },
    setPinMode(mode: PinMode) {
      setState((prev) => ({ ...prev, pinMode: mode }));
    },
    setActiveTab(tab: TerminalTab) {
      setState((prev) => ({ ...prev, activeTab: tab }));
    },
  };
}
